import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// material
import { styled, alpha } from '@mui/material/styles';
import { Box, Link, Drawer, Avatar, Chip, Typography } from '@mui/material';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
//
import { hasPermission, isAdminOrOwner } from '../../utils/hasPermission';
import navConfig from './NavConfig';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

// ── Sidebar wrapper ──────────────────────────────────────────────────────
const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

// ── Business account card ────────────────────────────────────────────────
const AccountCard = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  padding: theme.spacing(1, 1.25),
  borderRadius: 10,
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  textDecoration: 'none',
  transition: theme.transitions.create(['background-color', 'border-color']),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.09),
    borderColor: alpha(theme.palette.primary.main, 0.18),
  },
}));

// ── Sidebar paper overrides ──────────────────────────────────────────────
const drawerPaperSx = (theme) => ({
  width: DRAWER_WIDTH,
  bgcolor: 'background.paper',
  borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  boxShadow: 'none',
});

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'lg');
  const { user, activeApp } = useSelector((state) => state.auth);

  let selectedApp = activeApp;
  if (!selectedApp) {
    try {
      const bootstrapped = sessionStorage.getItem('bootstrappedApp');
      if (bootstrapped) {
        selectedApp = JSON.parse(bootstrapped);
      } else {
        const storedActive = localStorage.getItem('activeApp');
        if (storedActive) {
          selectedApp = JSON.parse(storedActive);
        }
      }
    } catch (e) {
      // ignore storage error
    }
  }

  const businessName = selectedApp?.name || 'Admin App';
  const businessLogo = selectedApp?.branding?.logoUrl || '';

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const isOwner =
    !selectedApp ||
    selectedApp.owner === user?._id ||
    selectedApp.owner === user?.id ||
    (selectedApp.owner &&
      typeof selectedApp.owner === 'object' &&
      (selectedApp.owner._id === user?._id || selectedApp.owner.id === user?._id));

  const isUserAdminOrOwner = isOwner || isAdminOrOwner(user, selectedApp);

  const filteredNavConfig = navConfig
    .filter((item) => {
      if (!item) return false;
      if (item?.isOwnerOnly && !isOwner) {
        return false;
      }
      if (item?.adminOrOwnerOnly && !isUserAdminOrOwner) {
        return false;
      }
      return !item?.permission || hasPermission(user, item?.permission);
    })
    .map((item) => {
      if (item.children && Array.isArray(item.children)) {
        const filteredChildren = item.children.filter((child) => {
          if (!child) return false;
          if (child?.isOwnerOnly && !isOwner) {
            return false;
          }
          if (child?.adminOrOwnerOnly && !isUserAdminOrOwner) {
            return false;
          }
          return !child?.permission || hasPermission(user, child?.permission);
        });
        return { ...item, children: filteredChildren };
      }
      return item;
    });

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* ── Logo strip ────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Logo logoUrl={selectedApp?.branding?.logoUrl || '/images/logo.png'} />
        <Chip
          label="Admin"
          size="small"
          color="primary"
          variant="soft"
          sx={{
            height: 20,
            fontSize: '0.6rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            color: 'primary.main',
            border: 'none',
          }}
        />
      </Box>

      {/* ── Business card ─────────────────────────────────────────────── */}
      <Box sx={{ px: 2, mb: 1.5 }}>
        <AccountCard underline="none" component={RouterLink} to="#">
          <Avatar src={businessLogo} alt={businessName} sx={{ width: 32, height: 32, borderRadius: 1.5 }} />
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.8125rem', lineHeight: 1.2 }}>
              {businessName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ textTransform: 'capitalize', fontSize: '0.6875rem', lineHeight: 1.2, display: 'block' }}>
              {user?.role}
            </Typography>
          </Box>
        </AccountCard>
      </Box>

      {/* ── Navigation ────────────────────────────────────────────────── */}
      <NavSection navConfig={filteredNavConfig} />

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <RootStyle>
      {/* ── Mobile drawer ─────────────────────────────────────────────── */}
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: (theme) => ({
              ...drawerPaperSx(theme),
              // Rounded right edge for mobile slide-in
              borderTopRightRadius: 20,
              borderBottomRightRadius: 20,
            }),
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {/* ── Desktop persistent drawer ──────────────────────────────────── */}
      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: drawerPaperSx,
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
